import { DiscussionSummary } from "@cnx/common";
import { Box, Text } from "@mantine/core";
import UserAvatar from "components/UserAvatar";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { getTimeAgoLabel } from "./chatRooms.utils";
import { LastMessagePreview } from "./LastMessagePreview";

interface PrivateDiscussionListItemProps {
  discussion: DiscussionSummary;
  currentUserId: string | null | undefined;
  typingMessage?: string;
  onSelect: (discussionId: string, contextId?: string | null) => void;
}

export const PrivateDiscussionListItem: FC<PrivateDiscussionListItemProps> = ({
  discussion,
  currentUserId,
  typingMessage,
  onSelect,
}) => {
  const { t } = useTranslation("discussions");

  const timeLabel = discussion.lastMessageCreatedTime
    ? getTimeAgoLabel(new Date(discussion.lastMessageCreatedTime).getTime(), t)
    : null;

  const isUnread =
    discussion.lastMessageCreatedBy !== currentUserId &&
    (discussion.unreadMessageCount ?? 0) > 0;

  return (
    <Box
      component="button"
      className="w-full px-3 border-t border-gray bg-white text-left cursor-pointer"
      style={{ paddingTop: 10, paddingBottom: 10 }}
      onClick={() => onSelect(discussion.id, discussion.contextId)}
    >
      <Box className="flex flex-row items-center">
        <Box className="shrink-0">
          <UserAvatar
            size={46}
            userName={discussion.participantName ?? ""}
            avatarId={discussion.participantAvatar ?? ""}
          />
        </Box>
        <Box className="pl-3 flex flex-col flex-1 min-w-0 gap-0.5">
          <Box className="flex flex-row items-baseline justify-between gap-2">
            <Text fz="md" fw={700} className="truncate flex-1 min-w-0">
              {discussion.participantName}
            </Text>
            <Box className="flex items-center gap-1.5 shrink-0">
              {timeLabel && (
                <Text
                  fz={12}
                  c={isUnread ? "var(--color-blue)" : "var(--color-gray-dark)"}
                >
                  {timeLabel}
                </Text>
              )}
              {isUnread && (
                <Box
                  bg="var(--color-blue)"
                  className="text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center shrink-0"
                >
                  {discussion.unreadMessageCount ?? ""}
                </Box>
              )}
            </Box>
          </Box>
          {discussion.participantEmail && (
            <Text fz="sm" c="var(--color-gray-dark)" className="truncate">
              {discussion.participantEmail}
            </Text>
          )}
          <LastMessagePreview
            discussion={discussion}
            currentUserId={currentUserId}
            typingMessage={typingMessage}
          />
        </Box>
      </Box>
    </Box>
  );
};
